import { useEffect, useState, useRef } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { uploadFile } from 'zite-file-upload-sdk';
import {
  getMyTrainingRequests, submitTrainingRequest, uploadTrainingCertificate,
  GetMyTrainingRequestsOutputType,
} from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Upload, CheckCircle2, Clock, XCircle, Award, Loader2, ExternalLink } from 'lucide-react';
import { format, parseISO } from 'date-fns';

type Request = GetMyTrainingRequestsOutputType['requests'][0];

const COURSE_TYPES = ['Cask Marque', 'Cellar Training', 'Punch Training', 'Personal Licence', 'Wine Training (WSET)', 'Management', 'Other'];

const statusConfig: Record<string, { label: string; color: string; icon: React.FC<{ className?: string }> }> = {
  Pending: { label: 'Pending', color: 'bg-muted text-muted-foreground', icon: Clock },
  Approved: { label: 'Approved', color: 'bg-primary/10 text-primary', icon: CheckCircle2 },
  Declined: { label: 'Declined', color: 'bg-destructive/10 text-destructive', icon: XCircle },
  Completed: { label: 'Completed', color: 'bg-primary/20 text-primary', icon: Award },
};

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy') : ''; } catch { return d || ''; } };
const fmtCurrency = (n?: number) => n != null ? `£${n.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '';

export default function TrainingRequests() {
  const { user } = useAuth();
  const [requests, setRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadTargetId, setUploadTargetId] = useState<string | null>(null);

  const [form, setForm] = useState({
    courseName: '',
    courseType: '',
    provider: '',
    estimatedCost: '',
    justification: '',
  });

  const load = () => {
    if (!user) return;
    getMyTrainingRequests({}).then(d => setRequests(d.requests)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [user]);

  const handleSubmit = async () => {
    if (!form.courseName.trim() || !form.courseType) {
      toast.error('Course name and type are required');
      return;
    }
    setSubmitting(true);
    try {
      await submitTrainingRequest({
        courseName: form.courseName,
        courseType: form.courseType,
        provider: form.provider || undefined,
        estimatedCost: form.estimatedCost ? parseFloat(form.estimatedCost) : undefined,
        justification: form.justification || undefined,
      });
      toast.success('Request submitted');
      setDialogOpen(false);
      setForm({ courseName: '', courseType: '', provider: '', estimatedCost: '', justification: '' });
      load();
    } catch (e: any) {
      toast.error(e.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadCert = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTargetId) return;
    setUploadingFor(uploadTargetId);
    try {
      const { fileUrl } = await uploadFile({ data: file, filename: file.name });
      await uploadTrainingCertificate({ requestId: uploadTargetId, certificateUrl: fileUrl });
      toast.success('Certificate uploaded');
      load();
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploadingFor(null);
      setUploadTargetId(null);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="px-6 md:px-8 py-8 max-w-3xl mx-auto space-y-3">
        <Skeleton className="h-8 w-48" />
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border">
        <div className="px-4 md:px-8 py-4 md:py-6 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-foreground">Training Requests</h1>
            <p className="text-sm text-muted-foreground mt-1">Request external training and track your certificates</p>
          </div>
          <Button onClick={() => setDialogOpen(true)} className="bg-primary text-primary-foreground">
            <Plus className="w-4 h-4 mr-1.5" /> New Request
          </Button>
        </div>
      </div>

      <div className="px-4 md:px-8 py-4 md:py-6 max-w-3xl pb-20 md:pb-6">
        {requests.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Award className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No training requests yet. Submit a request to get started.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map(req => {
              const cfg = statusConfig[req.status] || statusConfig.Pending;
              const StatusIcon = cfg.icon;
              return (
                <div key={req.id} className="border border-border rounded-lg bg-card p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="text-sm font-medium text-foreground">{req.courseName}</p>
                      {req.courseType && <p className="text-xs text-muted-foreground mt-0.5">{req.courseType}</p>}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-sm whitespace-nowrap flex items-center gap-1 ${cfg.color}`}>
                      <StatusIcon className="w-3 h-3" /> {cfg.label}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mb-2">
                    {req.provider && <span>Provider: {req.provider}</span>}
                    {req.estimatedCost != null && <span>Estimated: {fmtCurrency(req.estimatedCost)}</span>}
                    {req.confirmedCost != null && <span className="text-primary font-medium">Confirmed: {fmtCurrency(req.confirmedCost)}</span>}
                    {req.submittedAt && <span>Submitted {fmtDate(req.submittedAt)}</span>}
                    {req.completedDate && <span>Completed {fmtDate(req.completedDate)}</span>}
                  </div>

                  {req.reviewNotes && (
                    <p className="text-xs text-muted-foreground bg-muted/50 px-3 py-2 rounded mb-2 italic">"{req.reviewNotes}"</p>
                  )}

                  {/* Certificate section */}
                  {req.status === 'Approved' || req.status === 'Completed' ? (
                    <div className="mt-2">
                      {req.certificate.length > 0 ? (
                        <a
                          href={req.certificate[0].url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          <ExternalLink className="w-3 h-3" /> View Certificate
                        </a>
                      ) : req.status === 'Approved' ? (
                        <button
                          onClick={() => { setUploadTargetId(req.id); fileRef.current?.click(); }}
                          disabled={uploadingFor === req.id}
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50"
                        >
                          {uploadingFor === req.id
                            ? <><Loader2 className="w-3 h-3 animate-spin" /> Uploading...</>
                            : <><Upload className="w-3 h-3" /> Upload Certificate</>
                          }
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Hidden file input for certificate upload */}
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleUploadCert} />

      {/* New Request Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Training Course</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Course Type *</label>
              <Select value={form.courseType || 'none'} onValueChange={v => setForm(f => ({ ...f, courseType: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {COURSE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Course Name *</label>
              <Input
                value={form.courseName}
                onChange={e => setForm(f => ({ ...f, courseName: e.target.value }))}
                placeholder="e.g. WSET Level 2 Award in Wines"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Provider</label>
                <Input
                  value={form.provider}
                  onChange={e => setForm(f => ({ ...f, provider: e.target.value }))}
                  placeholder="Provider name"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Estimated Cost (£)</label>
                <Input
                  type="number"
                  value={form.estimatedCost}
                  onChange={e => setForm(f => ({ ...f, estimatedCost: e.target.value }))}
                  placeholder="0"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Why do you want this training?</label>
              <Textarea
                value={form.justification}
                onChange={e => setForm(f => ({ ...f, justification: e.target.value }))}
                placeholder="Explain how this course will benefit your role..."
                className="min-h-20"
              />
            </div>
            <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-primary text-primary-foreground">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : 'Submit Request'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
